/**
 * Filling the nomination form from a picked contact.
 *
 * `ListNominationContacts` returns, per contact, more than the name and email the form used to
 * take: company, job title, phone and annual revenue (CustomerLink lead fields, via
 * CR-308804591). Picking a contact copies those into the fields below so the BD does not retype
 * them.
 *
 * The rule that matters is what may be overwritten. "Only fill empty fields" is not enough: pick
 * A, A's company fills in; pick B instead, and the company field is no longer empty, so it would
 * keep A's company beside B's name — two people's details in one nomination. So a field is
 * treated as UNTOUCHED when it is empty or still holds exactly what the last fill put there, and
 * untouched fields always follow the current pick, including being cleared when the new contact
 * has no value for them. A field the BD has typed into is theirs and is never overwritten.
 *
 * The SFDC account id, company, job title, phone, email and the customer's BU are submitted (see
 * CreateNominationPage). The customer's BU fills from the contact's SFDC account sub BU
 * (`sfdcAccountSubBusinessUnit`) and goes out as `guestInformation.businessUnit`. Revenue is filled
 * for the BD's reference and dropped on submit — `CreateNomination` has no field for it. The contact's SFDC account tags are not a text field at all: they are carried
 * as-is onto `accountInformation.accountTags` (see contactAccountTags).
 *
 * Revenue: the SFDC account's (`sfdcAccountAnnualRevenue`, a pass-through string) when the
 * contact has one, else the lead's own self-reported `annualRevenue` (a number, and sparse).
 */

import type { Contact } from '../client/EventCalendarClient';
import { canonicalJobTitle } from './jobTitles';

/** The form fields a contact can fill. Contact Name is not here: it follows the search box. */
export const AUTOFILL_FIELDS = [
  'sfdcAccount',
  'companyName',
  'jobTitle',
  'phoneNumber',
  'email',
  'companyRevenueSize',
  'customerBu',
] as const;

export type AutofillField = (typeof AUTOFILL_FIELDS)[number];

/** Values a fill wrote, per field. Absent means the fill did not write that field. */
export type AutofillValues = Partial<Record<AutofillField, string>>;

/**
 * Revenue as the form shows it.
 *
 * Grouped digits, no currency: the service does not say which currency CustomerLink records, so
 * adding a symbol would be a guess presented as a fact.
 */
export function formatRevenue(revenue: number): string {
  return revenue.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * The SFDC account's revenue as the form shows it. The service passes it through as a string and
 * does not parse it, so it is formatted only when it IS a plain number, and shown as given
 * otherwise. Blank reads as absent.
 */
export function formatRevenueText(revenue: string | undefined): string | undefined {
  const raw = revenue?.trim();
  if (!raw) return undefined;
  const n = Number(raw);
  return /^-?\d+(\.\d+)?$/.test(raw) && Number.isFinite(n) ? formatRevenue(n) : raw;
}

/**
 * The picked contact's SFDC account tags, to submit as `accountInformation.accountTags`.
 * Undefined for no contact, or one with no SFDC account; `[]` for an account with no tags.
 * Passed through as-is: Customer Link may add tags, so they are never checked against a list.
 */
export function contactAccountTags(contact: Contact | undefined): string[] | undefined {
  return contact?.accountTags ? [...contact.accountTags] : undefined;
}

/**
 * What a contact would fill. Only fields it actually has a value for.
 *
 * Blank strings count as "no value" — the service passes CustomerLink's fields through, and a
 * whitespace company name would otherwise overwrite the field with nothing visible.
 */
export function contactValues(contact: Contact | undefined): AutofillValues {
  if (!contact) return {};
  const text = (v: string | undefined) => (v && v.trim() ? v.trim() : undefined);
  const values: AutofillValues = {
    sfdcAccount: text(contact.sfdcAccountId),
    companyName: text(contact.companyName),
    // Onto the form's list when it matches a key or label; kept as given when it does not.
    jobTitle: canonicalJobTitle(contact.jobTitle),
    customerBu: text(contact.sfdcAccountSubBusinessUnit),
    phoneNumber: text(contact.phone),
    email: text(contact.email),
    companyRevenueSize:
      formatRevenueText(contact.sfdcAccountAnnualRevenue) ??
      (typeof contact.annualRevenue === 'number' && Number.isFinite(contact.annualRevenue)
        ? formatRevenue(contact.annualRevenue)
        : undefined),
  };
  for (const key of AUTOFILL_FIELDS) if (values[key] === undefined) delete values[key];
  return values;
}

/**
 * The form after a (re)fill.
 *
 * @param current   the fields as they are now
 * @param previous  what the last fill wrote — how a field the BD edited is told apart from one
 *                  they left alone
 * @param next      what the new pick would fill; `{}` for "no contact matched", which clears
 *                  whatever the previous fill left untouched
 * @returns the new field values, and what this fill wrote (to pass as `previous` next time)
 */
export function applyAutofill(
  current: Readonly<Record<AutofillField, string>>,
  previous: AutofillValues,
  next: AutofillValues,
): { fields: Record<AutofillField, string>; filled: AutofillValues } {
  const fields = { ...current };
  const filled: AutofillValues = {};
  for (const key of AUTOFILL_FIELDS) {
    const untouched = current[key] === '' || current[key] === (previous[key] ?? '');
    if (!untouched) continue; // The BD's own value; never overwritten.
    fields[key] = next[key] ?? '';
    if (next[key] !== undefined) filled[key] = next[key];
  }
  return { fields, filled };
}
